import { useCallback, useEffect, useMemo } from "react"

import { TextFieldProps } from "@mui/material"
import { useField, useFormikContext } from "formik"

import * as Styled from "./FileNameTextField.styled"

export const FileNameTextField = (props: FileNameTextFieldProps) => {
    const {
        maxLength = 100,
        name = "",
        onChange,
        value,
        ...otherProps
    } = props

    const [fieldProps, metaProps] = useField({ name: name })
    const { setStatus } = useFormikContext()

    useEffect(() => {
        if (value?.length && value?.length > maxLength) {
            const parts = value?.split(".")
            if (!parts) {
                return
            }
            const extension = parts.length > 1 ? parts.pop() : ""
            const name = parts.join(".").substring(0, maxLength - (extension ? extension.length + 1 : 0))

            onChange(`${name}${extension ? "." + extension : ""}`)
        }
    }, [maxLength, value, onChange])

    const fileInfo = useMemo(() => {
        const parts = value?.split(".")
        if (!parts) {
            return {
                name: "",
                extension: "",
            }
        }
        const extension = parts.length > 1 ? parts.pop() : ""
        const name = parts.join(".").substring(0, maxLength - (extension ? extension.length + 1 : 0))

        return {
            name: name,
            extension: extension || "",
        }
    }, [maxLength, value])

    interface ChangeEventTarget {
        value: string
    }

    interface ChangeEvent {
        target: ChangeEventTarget
    }

    const handleChange = useCallback(
        (event: ChangeEvent) => {
            fieldProps.onChange(event)
            const newValue: string = event.target.value
            onChange(`${newValue}${fileInfo.extension ? "." + fileInfo.extension : ""}`)
            setStatus()
        },
        [fieldProps, setStatus, onChange, fileInfo]
    )

    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
            fieldProps.onBlur(event)
            props.onBlur?.(event)
        },
        [fieldProps, props]
    )

    return <Styled.TextField
        {...props}
        {...fieldProps}
        {...((metaProps.error && metaProps.touched) ? {
            error: true,
            helperText: metaProps.error,
        } : {})}
        onBlur={handleBlur}
        hasSeparator={!!fileInfo.extension}
        value={fileInfo.name || ""}
        onChange={handleChange}
        slotProps={{
            input: {
                endAdornment: (
                    fileInfo.extension.length ? <Styled.ExtensionAdornment variant="Desktop & Mobile/Body/Regular">{fileInfo.extension}</Styled.ExtensionAdornment> : undefined
                ),
            },
            htmlInput: {
                maxLength: maxLength - fileInfo.extension?.length,
            },
        }}
        {...otherProps}
    />
}

type FileNameTextFieldProps = TextFieldProps & {
    maxLength?: number,
    onBlur?: (_event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void,
    onChange: (_newValue: string) => void,
    value?: string,
}
